import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Position,
  MarkerType,
  NodeTypes,
  Handle,
  ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useTabNavigation } from '../../../shared/hooks/useTabNavigation';
import { calculateGenerationNumbers } from '../lib/generationUtils';

interface OdysseyJob {
  job_id: string;
  name: string | null;
  base_job_id: string | null;
  status: string;
  created_at: string | null;
  project_id: string | null;
  num_gens?: number | null;
}

interface GenerationFlowchartProps {
  odysseyJobs: OdysseyJob[];
}

// Custom node component
const GenerationNode = React.memo(({ data }: { data: any }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const nodeRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (isHovered && nodeRef.current) {
      const rect = nodeRef.current.getBoundingClientRect();
      setTooltipPos({
        x: rect.left + rect.width / 2,
        y: rect.top
      });
    }
  }, [isHovered]);
  
  return (
    <>
      <div
        ref={nodeRef}
        className="relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Handle type="target" position={Position.Left} className="opacity-0" />
        
        <div
          className="w-12 h-12 rounded-full bg-gray-500 text-white flex items-center justify-center font-semibold cursor-pointer transition-transform hover:scale-110 shadow-lg"
          onClick={data.onClick}
        >
          {data.generationNumber}
        </div>
        
        <Handle type="source" position={Position.Right} className="opacity-0" />
      </div>
      
      {isHovered && data.name && (() => {
        try {
          return createPortal(
            <div 
              className="fixed px-3 py-1 bg-gray-800 text-white text-sm rounded whitespace-nowrap pointer-events-none"
              style={{ 
                left: tooltipPos.x,
                top: tooltipPos.y - 36,
                transform: 'translateX(-50%)',
                zIndex: 10000
              }}
            >
              {data.name}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800" />
              </div>
            </div>,
            document.body
          );
        } catch (error) {
          console.error('Tooltip portal creation failed:', error);
          return null;
        }
      })()}
    </>
  );
});

// Define nodeTypes outside component to prevent recreation
const nodeTypes: NodeTypes = {
  generation: GenerationNode,
};

function GenerationFlowchartInner({ odysseyJobs }: GenerationFlowchartProps) {
  const { openOdysseyTab } = useTabNavigation();
  const containerRef = useRef<HTMLDivElement>(null);
  const reactFlowInstance = useRef<any>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const resizeTimeoutRef = useRef<number>();

  // Handle responsive fitView
  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      
      resizeTimeoutRef.current = window.setTimeout(() => {
        if (reactFlowInstance.current && isInitialized) {
          reactFlowInstance.current.fitView({ 
            padding: 0.05, 
            includeHiddenNodes: false,
            duration: 200 // Smooth animation
          });
        }
      }, 350); // Wait for sidebar animation to complete
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [isInitialized]);

  // Build tree structure and create nodes/edges
  const { nodes, edges } = useMemo(() => {
    if (!odysseyJobs || odysseyJobs.length === 0) {
      return { nodes: [], edges: [] };
    }
    
    // Filter to only show completed jobs in the flowchart visualization
    const completedJobs = odysseyJobs.filter(job => job.status === 'completed');
    
    if (completedJobs.length === 0) {
      return { nodes: [], edges: [] };
    }
    
    // Create a map for quick lookup
    const jobMap = new Map<string, OdysseyJob>();
    completedJobs.forEach(job => {
      jobMap.set(job.job_id, job);
    });
    
    // Calculate generation numbers for all jobs (not just completed) to ensure consistency
    // This ensures generation numbers are correct even if parent jobs are incomplete
    const generationNumbers = calculateGenerationNumbers(odysseyJobs);
    
    // Group jobs by generation
    const generationGroups = new Map<number, OdysseyJob[]>();
    completedJobs.forEach(job => {
      const gen = generationNumbers.get(job.job_id) || 1;
      if (!generationGroups.has(gen)) {
        generationGroups.set(gen, []);
      }
      generationGroups.get(gen)!.push(job);
    });
    
    // Create nodes with positions
    const nodes: Node[] = [];
    const nodePositions = new Map<string, { x: number; y: number }>();
    
    // Use fixed spacing that works well with fitView
    const horizontalSpacing = 180;
    const verticalSpacing = 100;
    
    // Calculate total height needed for centering
    const maxJobsInGeneration = Math.max(...Array.from(generationGroups.values()).map(jobs => jobs.length));
    
    Array.from(generationGroups.entries())
      .sort(([a], [b]) => a - b)
      .forEach(([generation, jobs]) => {
        const x = (generation - 1) * horizontalSpacing;
        
        // Center the jobs vertically within each generation
        const totalHeight = (jobs.length - 1) * verticalSpacing;
        const startY = -totalHeight / 2;
        
        jobs.forEach((job, index) => {
          const y = startY + (index * verticalSpacing);
          
          const node: Node = {
            id: job.job_id,
            type: 'generation',
            position: { x, y },
            data: {
              // Display the number of generations requested for this job (default 1)
              generationNumber: typeof job.num_gens === 'number' && job.num_gens > 0 ? job.num_gens : 1,
              name: job.name || `Generation ${generation}`,
              status: job.status,
              onClick: () => openOdysseyTab(job.job_id, job.name || 'Odyssey Job')
            }
          };
          
          nodes.push(node);
          nodePositions.set(job.job_id, { x, y });
        });
      });
    
    // Create edges (only between completed jobs)
    const edges: Edge[] = [];
    completedJobs.forEach(job => {
      if (job.base_job_id && nodePositions.has(job.base_job_id) && nodePositions.has(job.job_id)) {
        edges.push({
          id: `${job.base_job_id}-${job.job_id}`,
          source: job.base_job_id,
          target: job.job_id,
          type: 'smoothstep',
          animated: false,
          style: { 
            stroke: '#9CA3AF',
            strokeWidth: 2 
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#9CA3AF',
            width: 20,
            height: 20
          }
        });
      }
    });
    
    return { nodes, edges };
  }, [odysseyJobs, openOdysseyTab]);

  // Auto-adjust viewport when new nodes are added (new jobs complete)
  useEffect(() => {
    if (reactFlowInstance.current && isInitialized && nodes.length > 0) {
      // Trigger fitView when new nodes are added
      const timer = setTimeout(() => {
        reactFlowInstance.current.fitView({ 
          padding: 0.05, 
          includeHiddenNodes: false,
          duration: 200 // Smooth animation for new nodes
        });
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [nodes.length, isInitialized]);
  
  // Handle initial fitView after ReactFlow is ready
  const handleInit = useCallback((instance: any) => {
    reactFlowInstance.current = instance;
    // Delay initial fitView to ensure proper layout
    setTimeout(() => {
      instance.fitView({ 
        padding: 0.05, 
        includeHiddenNodes: false,
        duration: 0 // No animation for initial positioning
      });
      setIsInitialized(true);
    }, 100);
  }, []);

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    if (node.data.onClick) {
      node.data.onClick();
    }
  }, []);
  
  if (!odysseyJobs || odysseyJobs.length === 0 || nodes.length === 0) {
    return (
      <div className="h-96 w-full bg-gray-50 rounded-lg flex items-center justify-center text-gray-500">
        No completed generations to display
      </div>
    );
  }
  
  return (
    <div ref={containerRef} className="h-96 w-full bg-gray-50 rounded-lg overflow-visible">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        onInit={handleInit}
        attributionPosition="bottom-left"
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        minZoom={0.1}
        maxZoom={2}
      >
        <Background color="#E5E7EB" gap={16} />
      </ReactFlow>
    </div>
  );
}

export function GenerationFlowchart({ odysseyJobs }: GenerationFlowchartProps) {
  return (
    <ReactFlowProvider>
      <GenerationFlowchartInner odysseyJobs={odysseyJobs} />
    </ReactFlowProvider>
  );
}
